import { Component } from '@theme/component';

const REGION_KEY = 'echt-shipping-region';
const COUNTRY_KEY = 'echt-shipping-country';

/**
 * Echt · the shipping page's destination picker.
 *
 * Progressive enhancement only. The section renders every region's table into
 * the page and nothing is hidden until this component connects and sets
 * `data-ready` — the one attribute the stylesheet's hiding rule is keyed on. So
 * with this file blocked, still loading or thrown, the page is the full rate
 * card: nine regions, nine tables, no picker. `tests/visual/
 * pages-shipping-behaviour.spec.mjs` asserts that against the raw HTML.
 *
 * Storage is two keys rather than one. The region id is what the page shows and
 * what the section's `<option value>` carries, but every country in a region
 * shares that value, so restoring by region alone would put the select back on
 * the first country of the group — pick Japan, reload, read "Hong Kong". The
 * country is stored alongside purely to put the control back where the shopper
 * left it; the region remains the source of truth for what is displayed, and a
 * stored country that no longer exists falls back to it.
 *
 * @typedef {object} Refs
 * @property {HTMLSelectElement} select - The destination selector.
 * @extends {Component<Refs>}
 */
class EchtShippingRates extends Component {
  requiredRefs = ['select'];

  connectedCallback() {
    super.connectedCallback();

    const option = this.#initialOption();
    // `option.selected`, not `select.value = …`: every option in a region shares
    // the region's id as its value, so assigning the value lands on the first
    // country of the group. Pick Japan, reload, and the control reads
    // "Hong Kong" — right region, wrong answer to the question asked.
    if (option) option.selected = true;
    this.#show(option?.value ?? this.#regions[0]?.dataset.region);
    // `data-ready` goes on last: it is what hands the stylesheet permission to
    // hide a region, so setting it before one has been chosen would flash every
    // table off screen for a frame.
    this.dataset.ready = '';

    this.addEventListener('shopify:block:select', this.#onBlockSelect);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('shopify:block:select', this.#onBlockSelect);
  }

  /**
   * Theme editor: clicking a region in the block list must show that region.
   *
   * Eight of the nine are `display: none` at any moment, so without this a
   * merchandiser selects "Europe", the editor scrolls to it, and there is
   * nothing on screen to edit — the section looks broken and the block looks
   * empty. Shopify fires this on the block's own root element and it bubbles,
   * so the listener sits on the component and reads the region back off the
   * event's target; `event.detail.blockId` is the fallback for the case where
   * the target is a descendant the editor picked instead of the root.
   *
   * @param {Event} event
   */
  #onBlockSelect = (event) => {
    const target = event.target;
    const region = target instanceof Element ? target.closest('[data-echt-shipping="region"]') : null;
    const id =
      /** @type {HTMLElement | null} */ (region)?.dataset.region ??
      /** @type {CustomEvent} */ (event).detail?.blockId;
    if (!id) return;

    const option = [...this.refs.select.options].find((o) => o.value === id);
    if (option) option.selected = true;
    this.#show(id);
  };

  /** @returns {HTMLElement[]} the region sections, in document order. */
  get #regions() {
    return /** @type {HTMLElement[]} */ ([...this.querySelectorAll('[data-echt-shipping="region"]')]);
  }

  /**
   * The option to start on: the shopper's last choice, else their market, else
   * the first region. Every step is checked against the options actually in the
   * DOM, so a remembered region that has since been deleted in the theme editor
   * falls through rather than selecting nothing.
   *
   * @returns {HTMLOptionElement | undefined}
   */
  #initialOption() {
    const options = /** @type {HTMLOptionElement[]} */ ([...this.refs.select.options]);
    const stored = this.#read(REGION_KEY);

    if (stored) {
      const country = this.#read(COUNTRY_KEY);
      const exact = options.find((o) => o.value === stored && o.dataset.country === country);
      const remembered = exact ?? options.find((o) => o.value === stored);
      if (remembered) return remembered;
    }

    if (this.dataset.preselect !== 'false') {
      const market = (this.dataset.market ?? '').trim().toLowerCase();
      if (market) {
        // The shopper's own country first. Matching the region alone would put
        // a Japanese market on "Hong Kong" — right table, wrong country, and
        // exactly the defect the second storage key exists to avoid on the
        // remembered path.
        const country = options.find((o) => (o.dataset.country ?? '').trim().toLowerCase() === market);
        if (country) return country;

        // Then a region that lists the market but has no option for it. The two
        // cannot disagree today — both are generated from the same `countries`
        // setting — but `data-countries` is a public attribute of the block and
        // this fallback is what makes it mean what it says.
        const region = this.#regions.find((el) =>
          (el.dataset.countries ?? '')
            .split(',')
            .map((name) => name.trim().toLowerCase())
            .includes(market)
        );
        if (region) {
          const match = options.find((o) => o.value === region.dataset.region);
          if (match) return match;
        }
      }
    }

    return options[0];
  }

  /**
   * Handles the select's `change`. Focus is deliberately left alone: the
   * shopper is already on the control that did this, and moving focus to the
   * table they just asked for would strand a keyboard user outside the picker
   * they are likely to use again.
   *
   * @param {Event} event
   */
  select(event) {
    const option = /** @type {HTMLSelectElement} */ (event.target).selectedOptions[0];
    if (!option) return;
    this.#show(option.value);
    this.#write(REGION_KEY, option.value);
    this.#write(COUNTRY_KEY, option.dataset.country ?? '');
  }

  /**
   * Shows one region and hides the rest.
   *
   * @param {string | undefined} id
   */
  #show(id) {
    for (const region of this.#regions) region.hidden = region.dataset.region !== id;
  }

  /**
   * `localStorage` throws outright in Safari's private mode and wherever site
   * data is blocked, and a remembered destination is not worth taking the
   * picker down with it.
   *
   * @param {string} key
   * @returns {string | null}
   */
  #read(key) {
    try {
      return localStorage.getItem(key);
    } catch {
      return null;
    }
  }

  /**
   * @param {string} key
   * @param {string} value
   */
  #write(key, value) {
    try {
      localStorage.setItem(key, value);
    } catch {
      /* remembering the choice is optional; showing the table is not */
    }
  }
}

if (!customElements.get('echt-shipping-rates')) customElements.define('echt-shipping-rates', EchtShippingRates);
